/**
 * Copyright(c) Live2D Inc. All rights reserved.
 *
 * Use of this source code is governed by the Live2D Open Software license
 * that can be found at https://www.live2d.com/eula/live2d-open-software-license-agreement_en.html.
 */

import { LAppDelegate } from './lappdelegate';
import * as LAppDefine from './lappdefine';
import { LAppGlManager } from './lappglmanager';
import { LAppLive2DManager } from './lapplive2dmanager';
import { setGlobalResolutionTier, getGlobalResolutionTier } from './lappmodel';

type CameraPreset = {
  scale: number;
  duration: number;
  modelYOffset?: number;
  modelYOffsetDuration?: number;
  modelXOffset?: number;
  modelXOffsetDuration?: number;
};

const CAMERA_MODE_PRESETS: Record<number, CameraPreset> = {
  0: {
    scale: 0.9,
    duration: 500,
    modelYOffset: LAppDefine.ModelInitialYOffset,
    modelXOffset: 0
  },
  1: {
    scale: 1.08,
    duration: 1000,
    modelYOffset: LAppDefine.ModelZoomYOffset,
    modelXOffset: 0
  },
  2: {
    scale: 0.8,
    duration: 500,
    modelYOffset: 0.2,
    modelXOffset: 0
  }
};

const DEFAULT_LIGHT_TRANSITION_MS = 400;
const LIVE2D_RUNTIME_READY_CHANNEL = 'live2d_runtime_ready';

type FlutterPointerEventType = 'start' | 'move' | 'end' | 'cancel';

type FlutterPointerEventPayload = {
  type: FlutterPointerEventType;
  x: number;
  y: number;
  viewportWidth?: number;
  viewportHeight?: number;
};

type PointerCoordinateSnapshot = {
  canvasX: number;
  canvasY: number;
  pageX: number;
  pageY: number;
};

function notifyFlutterRuntimeReady(payload: Record<string, unknown> = {}): void {
  try {
    const bridge = (window as any)[LIVE2D_RUNTIME_READY_CHANNEL];
    if (!bridge || typeof bridge.postMessage !== 'function') {
      console.log('[Live2D] Runtime ready channel not available');
      return;
    }

    bridge.postMessage(
      JSON.stringify({
        type: 'runtime-ready',
        timestamp: Date.now(),
        ...payload
      })
    );
    console.log('[Live2D] Sent runtime ready signal to Flutter');
  } catch (error) {
    console.error('[Live2D] Failed to send runtime ready signal:', error);
  }
}

/**
 * ブラウザロード後の処理
 */
window.addEventListener(
  'load',
  (): void => {
    // Initialize WebGL and create the application instance
    if (
      !LAppGlManager.getInstance() ||
      !LAppDelegate.getInstance().initialize()
    ) {
      return;
    }

    LAppDelegate.getInstance().run();
    setupControlPanel();

    // Expose LAppLive2DManager for model readiness checks from Flutter
    window.LAppLive2DManager = LAppLive2DManager;
    notifyFlutterRuntimeReady({ status: 'initialized' });
  },
  { passive: true }
);

/**
 * 終了時の処理
 */
window.addEventListener(
  'beforeunload',
  (): void => LAppDelegate.releaseInstance(),
  { passive: true }
);

/**
 * Process when changing screen size.
 */
window.addEventListener(
  'resize',
  () => {
    if (LAppDefine.CanvasSize === 'auto') {
      LAppDelegate.getInstance().onResize();
    }
  },
  { passive: true }
);
declare global {
  interface Window {
    listExpression: (i?: number) => void;
    playExpression: (expressionName: string, i?: number) => void;
    listMotions: (i?: number) => void;
    playMotion: (motionNameOrFile: string, motionIdOrModelIndex?: number, modelIndex?: number) => void;
    startTalking: (speed?: number, intensity?: number, i?: number) => void;
    endTalking: (i?: number) => void;
    isTalking: (i?: number) => boolean;
    setTalkingSpeed: (i: number, speed: number) => void;
    setTalkingIntensity: (i: number, intensity: number) => void;
    // Channel Expression System functions
    playChannelExpression: (channel: string, expressionName: string, duration: number, i?: number) => Promise<void> | void;
    playChannelExpressionInstant: (channel: string, expressionName: string, i?: number) => Promise<void> | void;
    playMultipleChannelExpressions: (channel: string, expressionNames: string[], isInstant: boolean, i?: number) => Promise<void> | void;
    stopChannelExpression: (channel: string, i?: number) => void;
    getCurrentChannelExpression: (channel: string, i?: number) => string | null;
    getAllActiveChannelExpressions: (i?: number) => { [channel: string]: string };
    clearExpressionChannel: (channel: string, i?: number) => void;
    clearAllExpressionChannels: (i?: number) => void;
    getExpressionChannelDuration: (channel: string, i?: number) => number;
    // Resolution/Quality control
    setLive2DResolution: (tier: string) => Promise<boolean> | boolean;
    setLive2DResolutionTierOnly: (tier: string) => boolean;
    getLive2DResolutionTier: () => string | null;
    setPendingWardrobeExpressions: (
      snapshot: Record<string, string[] | string> | null
    ) => void;
    setPendingWardrobeExpressionPaths: (
      snapshot: Record<string, string> | null
    ) => void;
    pauseLive2DRendering: () => void;
    resumeLive2DRendering: () => void;
    hideLive2DCanvas: () => void;
    revealLive2DCanvas: () => void;
    isWardrobeSwapComplete: () => boolean;
    reloadCurrentModelWithResolution: () => Promise<boolean> | boolean;
    // Expose LAppLive2DManager for model readiness checks
    LAppLive2DManager: typeof LAppLive2DManager;
    // Model management
    changeModel: (modelId: string) => boolean;
    preloadModel: (modelId: string) => boolean;
    getCurrentModelId: () => string | null;
    listAvailableModels: () => string[];
    // Admin function to show control panel
    useAdmin: () => void;
    __Live2DPendingWardrobeExpressions?: Record<string, string[]> | null;
    __Live2DPendingWardrobeExpressionPaths?: Record<string, string> | null;
    __Live2DLoadStartTime?: number;
    __Live2DPendingWardrobeTextures?: { required: string[]; owned: string[] } | null;
    setPendingWardrobeTextures: (data: { required: string[]; owned: string[] } | null) => void;
    setWardrobeTextures: (required: string[], owned: string[], i?: number) => void;
    applyWardrobeBatch: (jsonStr: string) => Promise<string>;
    setCameraMode: (modeId: number) => boolean;
    setLightColor: (
      color: string | number | { r: number; g: number; b: number; a?: number },
      intensity?: number | string,
      transitionMs?: number | string
    ) => boolean;
    clearLightColor: (transitionMs?: number | string) => void;
    getLightColor: () => {
      color: { r: number; g: number; b: number; a: number };
      intensity: number;
      transitionMs: number;
    } | null;
    forwardFlutterTouchEvent: (payload: string | FlutterPointerEventPayload) => boolean;
  }
}

window.reloadCurrentModelWithResolution = async () => {
  const manager = LAppLive2DManager.getInstance();
  if (!manager) {
    console.error('[Live2D] LAppLive2DManager not initialized');
    return false;
  }

  return manager.reloadCurrentModelWithResolution();
};

window.setPendingWardrobeExpressions = (snapshot) => {
  if (!snapshot || typeof snapshot !== 'object') {
    (window as any).__Live2DPendingWardrobeExpressions = null;
    console.log('[Live2D] Cleared pending wardrobe expressions');
    return;
  }

  const normalized: Record<string, string[]> = {};

  for (const [channel, value] of Object.entries(snapshot)) {
    let expressions: string[] = [];
    if (Array.isArray(value)) {
      expressions = value
        .map((expr) => (typeof expr === 'string' ? expr.trim() : ''))
        .filter((expr) => expr.length > 0);
    } else if (typeof value === 'string') {
      expressions = value
        .split('+')
        .map((expr) => expr.trim())
        .filter((expr) => expr.length > 0);
    }

    if (expressions.length > 0) {
      normalized[channel] = expressions;
    }
  }

  const channelCount = Object.keys(normalized).length;
  (window as any).__Live2DPendingWardrobeExpressions =
    channelCount > 0 ? normalized : null;

  if (channelCount > 0) {
    console.log(
      `[Live2D] Registered pending wardrobe expressions for ${channelCount} channel${channelCount === 1 ? '' : 's'}`
    );
  } else {
    console.log('[Live2D] No valid wardrobe expressions supplied; clearing pending cache');
  }
};

window.setPendingWardrobeExpressionPaths = (snapshot) => {
  if (!snapshot || typeof snapshot !== 'object') {
    (window as any).__Live2DPendingWardrobeExpressionPaths = null;
    console.log('[Live2D] Cleared pending wardrobe expression paths');
    return;
  }

  const normalized: Record<string, string> = {};

  for (const [expressionName, expressionPath] of Object.entries(snapshot)) {
    if (typeof expressionName !== 'string' || typeof expressionPath !== 'string') {
      continue;
    }

    const normalizedName = expressionName.trim();
    const normalizedPath = expressionPath.trim();
    if (!normalizedName || !normalizedPath) {
      continue;
    }

    normalized[normalizedName] = normalizedPath;
  }

  const expressionCount = Object.keys(normalized).length;
  (window as any).__Live2DPendingWardrobeExpressionPaths =
    expressionCount > 0 ? normalized : null;

  if (expressionCount > 0) {
    console.log(
      `[Live2D] Registered pending wardrobe expression paths for ${expressionCount} expression${expressionCount === 1 ? '' : 's'}`
    );
  } else {
    console.log('[Live2D] No valid wardrobe expression paths supplied; clearing pending cache');
  }
};

// Register texture requirements before model load (parallel to setPendingWardrobeExpressions)
window.setPendingWardrobeTextures = (data) => {
  if (!data || typeof data !== 'object') {
    (window as any).__Live2DPendingWardrobeTextures = null;
    console.log('[Live2D] Cleared pending wardrobe textures');
    return;
  }
  (window as any).__Live2DPendingWardrobeTextures = data;
  console.log(`[Live2D] Registered pending wardrobe textures: ${data.required.length} required, ${data.owned.length} owned`);
};

// Apply wardrobe texture changes at runtime: loads newly required, unloads stale
window.setWardrobeTextures = (required: string[], owned: string[], i: number = 0) => {
  const models = LAppLive2DManager.getInstance().getActiveModels();
  if (models.length === 0) {
    console.error('[Live2D] Model not loaded yet, cannot set wardrobe textures');
    return;
  }
  console.log(
    `[Live2D] setWardrobeTextures called for ${models.length} sub-model(s): ` +
    `required=${required.join(', ') || '(none)'}; owned=${owned.join(', ') || '(none)'}`
  );
  for (const model of models) model.applyWardrobeTextures(required, owned);
};

/**
 * Batch wardrobe apply: loads missing expressions in parallel, applies all
 * channels in one shot, and handles texture swaps. Replaces 30+ individual
 * Dart→JS roundtrips with a single call.
 */
window.applyWardrobeBatch = async (jsonStr: string): Promise<string> => {
  try {
    const data = typeof jsonStr === 'string' ? JSON.parse(jsonStr) : jsonStr;
    const models = LAppLive2DManager.getInstance().getActiveModels();
    if (models.length === 0) return JSON.stringify({ error: 'no model' });

    // Apply wardrobe textures to all sub-models
    if (data.textures) {
      for (const model of models) {
        model.applyWardrobeTextures(
          data.textures.required || [],
          data.textures.owned || []
        );
      }
    }

    // Apply batch to all sub-models, merge results
    const results = await Promise.all(
      models.map(m => m.loadAndApplyWardrobeBatch(
        data.channels || {},
        data.paths || {}
      ))
    );
    // Return the first result (they should be similar)
    return JSON.stringify(results[0] ?? {});
  } catch (e) {
    return JSON.stringify({ error: String(e) });
  }
};

window.pauseLive2DRendering = () => {
  console.log("BB pauseLive2DRendering");
  const delegate = LAppDelegate.getInstance();
  if (delegate) {
    delegate.pauseRendering();
  }
};

window.resumeLive2DRendering = () => {
  setTimeout(() => {
    const delegate = LAppDelegate.getInstance();
    if (delegate) {
      delegate.resumeRendering();
    }
  }, 10);
};

// ─── Canvas visibility gate ───────────────────────────────────────────
// Primary anti-nude mechanism: CSS-level opacity controlled exclusively by Dart.
// The canvas is hidden whenever a model changes and only revealed after Dart
// confirms that ALL wardrobe textures + expressions are fully applied.
// Even if the WebGL render loop draws nude frames, they are invisible.
window.hideLive2DCanvas = () => {
  const c = document.querySelector('canvas');
  if (c) (c as HTMLElement).style.opacity = '0';
  console.log('[Live2D] Canvas hidden (Dart-controlled)');
};

window.revealLive2DCanvas = () => {
  // Resume rendering if paused so the correctly-dressed model can draw
  const delegate = LAppDelegate.getInstance();
  if (delegate && delegate.isRenderingPaused()) {
    delegate.resumeRendering();
  }
  // Wait two frames so the correctly-dressed model renders before revealing
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      const c = document.querySelector('canvas');
      if (c) (c as HTMLElement).style.opacity = '1';
      console.log('[Live2D] Canvas revealed (Dart-controlled)');
    });
  });
};

window.isWardrobeSwapComplete = () => {
  const manager = LAppLive2DManager.getInstance();
  if (!manager) return true;
  // All sub-models must have completed their texture swaps
  const models = manager.getActiveModels();
  return models.length === 0 || models.every(m => !m.isTextureSwapActive);
};

window.setCameraMode = (modeId: number): boolean => {
  const preset = CAMERA_MODE_PRESETS[modeId] ?? CAMERA_MODE_PRESETS[0];
  const delegate = LAppDelegate.getInstance();

  if (!delegate) {
    console.error('[Live2D] LAppDelegate not initialized, cannot set camera');
    return false;
  }

  const view = delegate.getView();
  if (!view || typeof view.setCameraPreset !== 'function') {
    console.error('[Live2D] LAppView not ready, cannot set camera');
    return false;
  }

  view.setCameraPreset(preset.scale, preset.duration);
  const manager = LAppLive2DManager.getInstance();
  if (manager && typeof manager.setModelYOffset === 'function') {
    const targetOffset =
      preset.modelYOffset ?? LAppDefine.ModelInitialYOffset ?? 0;
    const offsetDuration =
      preset.modelYOffsetDuration ?? preset.duration ?? 0;
    manager.setModelYOffset(targetOffset, offsetDuration);
  }
  if (manager && typeof manager.setModelXOffset === 'function') {
    const targetXOffset = preset.modelXOffset ?? 0;
    const xOffsetDuration = preset.modelXOffsetDuration ?? preset.duration ?? 0;
    manager.setModelXOffset(targetXOffset, xOffsetDuration);
  }
  return true;
};

window.setLightColor = (
  color: string | number | { r: number; g: number; b: number; a?: number },
  intensity: number | string = 1,
  transitionMs: number | string = DEFAULT_LIGHT_TRANSITION_MS
): boolean => {
  const manager = LAppLive2DManager.getInstance();
  if (!manager) {
    console.error('[Live2D] LAppLive2DManager not initialized');
    return false;
  }

  return manager.setModelLightColor(color, intensity, transitionMs);
};

const VALID_POINTER_EVENT_TYPES: ReadonlySet<FlutterPointerEventType> = new Set([
  'start',
  'move',
  'end',
  'cancel'
]);

const toNumber = (value: unknown, fallback = 0): number => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const getCanvasElement = (): HTMLCanvasElement | null => {
  return document.querySelector('canvas');
};

const computePointerCoordinates = (
  payload: FlutterPointerEventPayload,
  canvas: HTMLCanvasElement
): PointerCoordinateSnapshot => {
  const rect = canvas.getBoundingClientRect();
  const viewportWidth = toNumber(
    payload.viewportWidth,
    window.innerWidth || rect.width || canvas.clientWidth || 1
  );
  const viewportHeight = toNumber(
    payload.viewportHeight,
    window.innerHeight || rect.height || canvas.clientHeight || 1
  );
  const normalizedX = viewportWidth > 0 ? toNumber(payload.x) / viewportWidth : 0;
  const normalizedY = viewportHeight > 0 ? toNumber(payload.y) / viewportHeight : 0;
  const canvasWidth = rect.width || canvas.clientWidth || 1;
  const canvasHeight = rect.height || canvas.clientHeight || 1;
  const canvasX = normalizedX * canvasWidth;
  const canvasY = normalizedY * canvasHeight;
  const scrollX = window.scrollX || window.pageXOffset || 0;
  const scrollY = window.scrollY || window.pageYOffset || 0;

  return {
    canvasX,
    canvasY,
    pageX: rect.left + scrollX + canvasX,
    pageY: rect.top + scrollY + canvasY
  };
};

const forwardPointerToLive2D = (
  eventType: FlutterPointerEventType,
  coords: PointerCoordinateSnapshot
): boolean => {
  const delegate = LAppDelegate.getInstance();
  if (!delegate || typeof delegate.getView !== 'function') {
    return false;
  }

  const view = delegate.getView();
  if (!view) {
    return false;
  }

  const delegateState = delegate as LAppDelegate & { _captured?: boolean };

  if (eventType === 'start') {
    delegateState._captured = true;
    view.onTouchesBegan(coords.canvasX, coords.canvasY);
    return true;
  }

  if (!delegateState._captured) {
    return false;
  }

  if (eventType === 'move') {
    view.onTouchesMoved(coords.canvasX, coords.canvasY);
    return true;
  }

  if (eventType === 'end' || eventType === 'cancel') {
    delegateState._captured = false;
    view.onTouchesEnded(coords.canvasX, coords.canvasY);
    return true;
  }

  return false;
};

window.forwardFlutterTouchEvent = (
  payload: string | FlutterPointerEventPayload
): boolean => {
  try {
    if (!payload) {
      return false;
    }

    const data: Partial<FlutterPointerEventPayload> =
      typeof payload === 'string' ? JSON.parse(payload) : payload;

    if (!data || typeof data !== 'object') {
      return false;
    }

    const type = String(data.type ?? '').toLowerCase() as FlutterPointerEventType;
    if (!VALID_POINTER_EVENT_TYPES.has(type)) {
      return false;
    }

    const canvas = getCanvasElement();
    if (!canvas) {
      return false;
    }

    const coords = computePointerCoordinates(
      {
        type,
        x: toNumber(data.x),
        y: toNumber(data.y),
        viewportWidth: data.viewportWidth,
        viewportHeight: data.viewportHeight
      },
      canvas
    );

    return forwardPointerToLive2D(type, coords);
  } catch (error) {
    console.warn('[Live2D] forwardFlutterTouchEvent failed', error);
    return false;
  }
};

window.clearLightColor = (
  transitionMs: number | string = DEFAULT_LIGHT_TRANSITION_MS
): void => {
  const manager = LAppLive2DManager.getInstance();
  if (!manager) {
    console.error('[Live2D] LAppLive2DManager not initialized');
    return;
  }

  manager.clearModelLightColor(transitionMs);
};

window.getLightColor = (): {
  color: { r: number; g: number; b: number; a: number };
  intensity: number;
  transitionMs: number;
} | null => {
  const manager = LAppLive2DManager.getInstance();
  if (!manager) {
    console.error('[Live2D] LAppLive2DManager not initialized');
    return null;
  }

  return manager.getCurrentModelLightColor();
};
window.listExpression = (i: number = 0) => {
  // Union expressions from all active sub-models
  for (const model of LAppLive2DManager.getInstance().getActiveModels()) {
    if (model._modelSetting) console.log(model._expressions);
  }
}
window.playExpression = (expressionName: string, i: number = 0) => {
  for (const model of LAppLive2DManager.getInstance().getActiveModels()) {
    if (model._modelSetting) model.setExpression(expressionName);
  }
}
window.listMotions = (i: number = 0) => {
  for (const model of LAppLive2DManager.getInstance().getActiveModels()) {
    if (model._modelSetting) console.log(model._motions);
  }
}

/**
 * Start a motion with flexible parameters:
 * - playMotion(fileName, modelIndex?) - e.g., playMotion("Idle_00", 0) or playMotion("Idle_00.motion3.json")
 * - playMotion(motionName, motionId, modelIndex?) - e.g., playMotion("Idle", 0, 0)
 */
window.playMotion = (motionNameOrFile: string, motionIdOrModelIndex?: number, modelIndex?: number) => {
  const manager = LAppLive2DManager.getInstance();
  const models = manager.getActiveModels();
  if (models.length === 0) {
    console.error('Model not loaded yet - cannot play motion');
    return;
  }

  // Use the first sub-model to resolve the motion name/id
  const primaryModel = models[0];
  let motionName: string;
  let motionId: number;

  if (modelIndex !== undefined) {
    // Old way: playMotion(motionName, motionId, modelIndex)
    motionName = motionNameOrFile;
    motionId = motionIdOrModelIndex!;
  } else if (motionIdOrModelIndex !== undefined && typeof motionIdOrModelIndex === 'number') {
    if (primaryModel._modelSetting) {
      const motionInfo = primaryModel.findMotionByFileName(motionNameOrFile);
      if (motionInfo) {
        motionName = motionInfo.group;
        motionId = motionInfo.index;
      } else {
        // Treat as old way: playMotion(motionName, motionId)
        motionName = motionNameOrFile;
        motionId = motionIdOrModelIndex;
      }
    } else {
      motionName = motionNameOrFile;
      motionId = motionIdOrModelIndex;
    }
  } else {
    // New way: playMotion(fileName)
    if (!primaryModel._modelSetting) {
      console.error('Model not loaded yet - cannot play motion');
      return;
    }
    const motionInfo = primaryModel.findMotionByFileName(motionNameOrFile);
    if (motionInfo) {
      motionName = motionInfo.group;
      motionId = motionInfo.index;
    } else {
      console.error(`Motion file not found: ${motionNameOrFile}`);
      return;
    }
  }

  // Play motion on ALL sub-models so they animate together
  for (const model of models) {
    if (model._modelSetting) {
      model.playMotion(motionName, motionId, 100);
    }
  }
}

// Talking animation global functions — route to ALL active sub-models
window.startTalking = (speed: number = 4.0, intensity: number = 0.6, i: number = 0) => {
  const models = LAppLive2DManager.getInstance().getActiveModels();
  if (models.length === 0) {
    console.error('Model not loaded yet');
    return;
  }
  for (const model of models) model.startTalking(speed, intensity);
}

window.endTalking = (i: number = 0) => {
  const models = LAppLive2DManager.getInstance().getActiveModels();
  if (models.length === 0) {
    console.error('Model not loaded yet');
    return;
  }
  for (const model of models) model.endTalking();
}

window.isTalking = (i: number = 0) => {
  const models = LAppLive2DManager.getInstance().getActiveModels();
  if (models.length === 0) {
    return false;
  }
  return models.some(m => m.isTalking());
}

window.setTalkingSpeed = (i: number, speed: number) => {
  for (const model of LAppLive2DManager.getInstance().getActiveModels()) {
    model.setTalkingSpeed(speed);
  }
}

window.setTalkingIntensity = (i: number, intensity: number) => {
  for (const model of LAppLive2DManager.getInstance().getActiveModels()) {
    model.setTalkingIntensity(intensity);
  }
}

// Channel Expression System global functions — route to ALL active sub-models.
// Each sub-model has its own expression manifest; expressions that don't exist
// on a given sub-model are silently ignored.
window.playChannelExpression = async (channel: string, expressionName: string, duration: number, i: number = 0) => {
  const models = LAppLive2DManager.getInstance().getActiveModels();
  if (models.length === 0) { console.error('Model not loaded yet'); return; }
  await Promise.all(models.map(m => m.ensureExpressionLoaded(expressionName).catch(() => {})));
  for (const model of models) model.playExpression(channel, expressionName, duration);
}

window.playChannelExpressionInstant = async (channel: string, expressionName: string, i: number = 0) => {
  const models = LAppLive2DManager.getInstance().getActiveModels();
  if (models.length === 0) { console.error('Model not loaded yet'); return; }
  await Promise.all(models.map(m => m.ensureExpressionLoaded(expressionName).catch(() => {})));
  for (const model of models) model.playExpressionInstant(channel, expressionName);
}

window.playMultipleChannelExpressions = async (channel: string, expressionNames: string[], isInstant: boolean, i: number = 0) => {
  const models = LAppLive2DManager.getInstance().getActiveModels();
  if (models.length === 0) { console.error('Model not loaded yet'); return; }
  await Promise.all(
    models.flatMap(m => expressionNames.map(name => m.ensureExpressionLoaded(name).catch(() => {})))
  );
  for (const model of models) model.playMultipleExpressions(channel, expressionNames, isInstant);
}

window.stopChannelExpression = (channel: string, i: number = 0) => {
  for (const model of LAppLive2DManager.getInstance().getActiveModels()) {
    model.stopExpression(channel);
  }
}

window.getCurrentChannelExpression = (channel: string, i: number = 0) => {
  // Return result from first sub-model that has the channel
  for (const model of LAppLive2DManager.getInstance().getActiveModels()) {
    const result = model.getCurrentExpression(channel);
    if (result != null) return result;
  }
  return null;
}

window.getAllActiveChannelExpressions = (i: number = 0) => {
  // Merge active expressions from all sub-models
  const merged: Record<string, any> = {};
  for (const model of LAppLive2DManager.getInstance().getActiveModels()) {
    const active = model.getAllActiveExpressions();
    if (active) Object.assign(merged, active);
  }
  return merged;
}

window.clearExpressionChannel = (channel: string, i: number = 0) => {
  for (const model of LAppLive2DManager.getInstance().getActiveModels()) {
    model.clearChannel(channel);
  }
}

window.clearAllExpressionChannels = (i: number = 0) => {
  for (const model of LAppLive2DManager.getInstance().getActiveModels()) {
    model.clearAllExpressions();
  }
}

window.getExpressionChannelDuration = (channel: string, i: number = 0) => {
  // Return max duration across all sub-models
  let maxDuration = 0;
  for (const model of LAppLive2DManager.getInstance().getActiveModels()) {
    const d = model.getChannelDuration(channel);
    if (d > maxDuration) maxDuration = d;
  }
  return maxDuration;
}

// Resolution/Quality control function
window.setLive2DResolution = async (tier: string) => {
  try {
    console.log(`[Live2D] Setting resolution tier to: ${tier}`);

    // Validate tier
    if (!['low', 'medium', 'high'].includes(tier)) {
      console.warn(`[Live2D] Unknown resolution tier: ${tier}, defaulting to high`);
      tier = 'high';
    }

    // Set the global resolution preference
    setGlobalResolutionTier(tier);

    // Get the current model to reload it with new textures
    const manager = LAppLive2DManager.getInstance();
    if (!manager) {
      console.error('[Live2D] LAppLive2DManager not initialized');
      return false;
    }

    const currentModelId = window.getCurrentModelId();
    if (!currentModelId) {
      console.warn('[Live2D] No model currently loaded, resolution will apply on next model load');
      return true;
    }

    console.log(`[Live2D] Reloading model "${currentModelId}" with ${tier} resolution textures...`);

    // Use smooth transition to keep old model visible while loading new one
    const success = await manager.reloadCurrentModelWithResolution();

    if (success) {
      console.log(`[Live2D] ✓ Resolution changed to ${tier} successfully (smooth transition)`);
    } else {
      console.error(`[Live2D] ✗ Failed to reload model with new resolution`);
    }

    return success;
  } catch (error) {
    console.error('[Live2D] Failed to set resolution:', error);
    return false;
  }
}

window.setLive2DResolutionTierOnly = (tier: string) => {
  try {
    if (!['low', 'medium', 'high'].includes(tier)) {
      console.warn(`[Live2D] Unknown resolution tier: ${tier}, ignoring request.`);
      return false;
    }

    console.log(`[Live2D] Updating global resolution tier to ${tier} without reload`);
    setGlobalResolutionTier(tier);
    return true;
  } catch (error) {
    console.error('[Live2D] Failed to set resolution tier (no reload):', error);
    return false;
  }
};

window.getLive2DResolutionTier = () => {
  try {
    return getGlobalResolutionTier();
  } catch (error) {
    console.error('[Live2D] Failed to get resolution tier:', error);
    return null;
  }
};

// Model management functions
window.changeModel = (modelId: string) => {
  // Track load start time for timing measurements
  (window as any).__Live2DLoadStartTime = performance.now();

  const manager = LAppLive2DManager.getInstance();
  if (manager == null) {
    console.error('LAppLive2DManager not initialized');
    return false;
  }
  return manager.changeModel(modelId);
}

//delay 5s, then call changeModel

window.getCurrentModelId = () => {
  const manager = LAppLive2DManager.getInstance();
  if (manager == null) {
    console.error('LAppLive2DManager not initialized');
    return null;
  }

  if (typeof manager.getCurrentModelId === 'function') {
    return manager.getCurrentModelId();
  }

  const index = manager['_sceneIndex'];
  return LAppDefine.ModelDir[index] || null;
}

window.listAvailableModels = () => {
  return LAppDefine.ModelDir;
}

// Debug: dynamically change splitAt for a sub-model's render passes
window.setSplitAt = (subKey: string, splitAt: number) => {
  const mgr = LAppLive2DManager.getInstance();
  if (!mgr) {
    console.error('[Live2D] LAppLive2DManager not initialized');
    return false;
  }
  mgr.setSplitAt(subKey, splitAt);
  return true;
};

window.getSubModelDrawableCount = (subKey: string): number => {
  const mgr = LAppLive2DManager.getInstance();
  if (!mgr) return 0;
  return mgr.getSubModelDrawableCount(subKey);
};

// Admin function to enable the control panel
window.useAdmin = () => {
  const panel = document.getElementById('control-panel');
  if (panel) {
    panel.classList.add('admin-mode');
    console.log('✅ Admin mode enabled - Control panel is now visible');
    console.log('Available commands: listExpression(), playExpression(name), listMotions(), playMotion(group, id)');
  } else {
    console.error('Control panel not found');
  }

  // In admin mode, patch changeModel to auto-reveal after loading
  const originalChangeModel = window.changeModel;
  window.changeModel = (modelId: string) => {
    const result = originalChangeModel(modelId);
    if (result) {
      setTimeout(() => {
        window.resumeLive2DRendering();
        window.revealLive2DCanvas();
        console.log('[Admin] Auto-revealed canvas after changeModel');
      }, 5000);
    }
    return result;
  };
  console.log('🔧 changeModel() will now auto-reveal canvas after 5s in admin mode');
}

function setupControlPanel(): void {
  const panel = document.getElementById('control-panel');

  // Create texture status overlay (top-left, admin-only)
  let textureOverlay = document.getElementById('texture-overlay') as HTMLDivElement | null;
  if (!textureOverlay) {
    textureOverlay = document.createElement('div');
    textureOverlay.id = 'texture-overlay';
    Object.assign(textureOverlay.style, {
      position: 'fixed',
      top: '8px',
      left: '8px',
      zIndex: '9999',
      background: 'rgba(0,0,0,0.72)',
      color: '#fff',
      fontFamily: 'monospace',
      fontSize: '11px',
      lineHeight: '1.5',
      padding: '6px 10px',
      borderRadius: '6px',
      pointerEvents: 'none',
      display: 'none',
      whiteSpace: 'pre',
      maxWidth: '260px',
    });
    document.body.appendChild(textureOverlay);
  }

  const expressionContainer = document.getElementById('expression-buttons');
  const motionContainer = document.getElementById('motion-buttons');
  const expressionEmpty = document.getElementById('expression-empty');
  const motionEmpty = document.getElementById('motion-empty');

  if (!panel || !expressionContainer || !motionContainer) {
    return;
  }

  let lastSignature = '';

  const formatLabel = (name: string): string => {
    if (!name) {
      return 'Unknown';
    }
    const lastSegment = name.split(/[\\/]/).pop() ?? name;
    const withoutExtension = lastSegment
      .replace(/\.motion3\.json$/i, '')
      .replace(/\.exp3\.json$/i, '')
      .replace(/\.moc3$/i, '')
      .replace(/\.json$/i, '');
    return withoutExtension.replace(/[_-]+/g, ' ');
  };

  const refreshControls = () => {
    const manager = LAppLive2DManager.getInstance();
    if (!manager) {
      return;
    }

    const model = manager.getModel(0);
    if (!model || !model._modelSetting) {
      return;
    }

    // Texture status overlay — only shown in admin mode
    const isAdmin = panel?.classList.contains('admin-mode') ?? false;
    if (textureOverlay) {
      if (isAdmin && typeof model.getTextureStatus === 'function') {
        const slots = model.getTextureStatus();
        const lines = ['── Textures ──────────────────'];
        for (const s of slots) {
          let icon: string;
          let tag: string;
          if (s.owned) {
            icon = s.loaded ? '🟢' : '⬜';
            tag = s.loaded ? 'loaded' : 'transp';
          } else if (s.manifested) {
            icon = '🟡';
            tag = 'manifest';
          } else {
            icon = '🟦';
            tag = 'base';
          }
          lines.push(`${icon} [${String(s.slot).padStart(2, '0')}] ${s.basename.padEnd(16)} ${tag}`);
        }
        lines.push('─────────────────────────────');
        lines.push('🟦 base  🟡 manifest  🟢 loaded  ⬜ transp');
        textureOverlay.textContent = lines.join('\n');
        textureOverlay.style.display = 'block';
      } else {
        textureOverlay.style.display = 'none';
      }
    }

    // Use dynamically loaded expression names (from manifest subfolder system)
    // falling back to model3.json list if the getter isn't available yet
    const expressions: string[] = typeof model.getLoadedExpressionNames === 'function'
      ? Array.from(model.getLoadedExpressionNames()).sort()
      : (() => {
          const count = typeof model._modelSetting.getExpressionCount === 'function'
            ? model._modelSetting.getExpressionCount() : 0;
          const names: string[] = [];
          for (let i = 0; i < count; i++) names.push(model._modelSetting.getExpressionName(i));
          return names;
        })();

    const motionGroups: { name: string; motions: { index: number; file: string }[] }[] = [];
    const motionGroupCount = typeof model._modelSetting.getMotionGroupCount === 'function'
      ? model._modelSetting.getMotionGroupCount()
      : 0;

    for (let gi = 0; gi < motionGroupCount; gi++) {
      const groupName = model._modelSetting.getMotionGroupName(gi);
      const motionCount = model._modelSetting.getMotionCount(groupName);
      const entries: { index: number; file: string }[] = [];

      for (let mi = 0; mi < motionCount; mi++) {
        const fileName = model._modelSetting.getMotionFileName(groupName, mi);
        entries.push({ index: mi, file: fileName });
      }

      motionGroups.push({ name: groupName, motions: entries });
    }

    const signature = JSON.stringify({
      expressions,
      motions: motionGroups.map((group) => ({
        name: group.name,
        files: group.motions.map((motion) => motion.file),
      })),
    });

    if (signature === lastSignature) {
      return;
    }

    lastSignature = signature;

    expressionContainer.innerHTML = '';
    if (expressions.length === 0) {
      if (expressionEmpty) {
        expressionEmpty.removeAttribute('hidden');
      }
    } else {
      if (expressionEmpty) {
        expressionEmpty.setAttribute('hidden', '');
      }

      expressions.forEach((expressionName) => {
        const button = document.createElement('button');
        button.className = 'control-button';
        button.textContent = formatLabel(expressionName);
        button.title = expressionName;
        button.addEventListener('click', () => {
          // Check if model is still loaded before playing
          const currentModel = LAppLive2DManager.getInstance().getModel(0);
          if (!currentModel || !currentModel._modelSetting) {
            console.error('Model not loaded yet - cannot play expression');
            return;
          }
          window.playExpression(expressionName);
        });
        expressionContainer.appendChild(button);
      });
    }

    motionContainer.innerHTML = '';
    if (motionGroups.length === 0) {
      if (motionEmpty) {
        motionEmpty.removeAttribute('hidden');
      }
      return;
    }

    if (motionEmpty) {
      motionEmpty.setAttribute('hidden', '');
    }

    motionGroups.forEach((group) => {
      const wrapper = document.createElement('div');
      wrapper.className = 'motion-group';

      const heading = document.createElement('div');
      heading.className = 'group-title';
      heading.textContent = group.name;
      wrapper.appendChild(heading);

      const grid = document.createElement('div');
      grid.className = 'button-grid';

      group.motions.forEach(({ index, file }) => {
        const button = document.createElement('button');
        button.className = 'control-button';
        const labelSource = file || `${group.name}_${index}`;
        const label = formatLabel(labelSource) || `${group.name} ${index + 1}`;
        button.textContent = label;
        button.title = labelSource;
        button.addEventListener('click', () => {
          // Check if model is still loaded before playing
          const currentModel = LAppLive2DManager.getInstance().getModel(0);
          if (!currentModel || !currentModel._modelSetting) {
            console.error('Model not loaded yet - cannot play motion');
            return;
          }
          window.playMotion(group.name, index);
        });
        grid.appendChild(button);
      });

      wrapper.appendChild(grid);
      motionContainer.appendChild(wrapper);
    });
  };

  const REFRESH_INTERVAL_MS = 800;
  refreshControls();
  window.setInterval(refreshControls, REFRESH_INTERVAL_MS);

  // --- SplitAt debug slider (standalone floating overlay) ---
  setupSplitAtOverlay();

  // --- Frame profiler overlay (admin only) ---
  setupProfilingOverlay();
}

/**
 * Creates a standalone floating overlay with splitAt sliders.
 * Auto-shows whenever the current model has split render passes.
 * Only visible in admin mode.
 */
function setupSplitAtOverlay(): void {
  // Create the floating container
  const overlay = document.createElement('div');
  overlay.id = 'splitAt-debug-overlay';
  Object.assign(overlay.style, {
    position: 'fixed',
    bottom: '16px',
    left: '16px',
    width: '280px',
    background: 'rgba(0, 0, 0, 0.75)',
    backdropFilter: 'blur(6px)',
    color: '#fff',
    fontFamily: "'Segoe UI', system-ui, sans-serif",
    fontSize: '13px',
    padding: '12px 14px',
    borderRadius: '10px',
    boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
    zIndex: '99999',
    display: 'none',
    userSelect: 'none',
  });
  document.body.appendChild(overlay);

  const title = document.createElement('div');
  title.textContent = 'Split At Debug';
  title.style.cssText = 'font-weight:600;font-size:14px;margin-bottom:10px;opacity:0.9;';
  overlay.appendChild(title);

  const slidersContainer = document.createElement('div');
  slidersContainer.id = 'splitAt-sliders';
  overlay.appendChild(slidersContainer);

  let lastSig = '';

  const refresh = () => {
    const panel = document.getElementById('control-panel');
    const isAdmin = panel?.classList.contains('admin-mode') ?? false;
    if (!isAdmin) {
      overlay.style.display = 'none';
      lastSig = '';
      return;
    }

    const manager = LAppLive2DManager.getInstance();
    if (!manager) {
      overlay.style.display = 'none';
      return;
    }

    const passes = manager.getRenderPasses();
    if (!passes || passes.length === 0) {
      overlay.style.display = 'none';
      lastSig = '';
      return;
    }

    // Find sub-models that have split passes (2 passes per subKey)
    const subKeyCounts = new Map<string, number>();
    for (const p of passes) {
      subKeyCounts.set(p.subKey, (subKeyCounts.get(p.subKey) || 0) + 1);
    }

    const splitSubKeys: string[] = [];
    for (const [key, count] of subKeyCounts) {
      if (count === 2) splitSubKeys.push(key);
    }

    if (splitSubKeys.length === 0) {
      overlay.style.display = 'none';
      lastSig = '';
      return;
    }

    overlay.style.display = '';

    // Rebuild sliders only when sub-model set changes
    const sig = splitSubKeys.join(',');
    if (sig === lastSig) return;
    lastSig = sig;

    slidersContainer.innerHTML = '';

    for (const subKey of splitSubKeys) {
      const drawableCount = manager.getSubModelDrawableCount(subKey);
      const backPass = passes.find(p => p.subKey === subKey && p.minOrder === 0);
      const currentSplitAt = backPass ? backPass.maxOrder : 0;
      const maxVal = drawableCount > 0 ? drawableCount : 3000;

      const wrapper = document.createElement('div');
      wrapper.style.marginBottom = '10px';

      const labelRow = document.createElement('div');
      labelRow.style.cssText = 'display:flex;justify-content:space-between;align-items:center;margin-bottom:4px;';

      const nameLabel = document.createElement('span');
      nameLabel.style.cssText = 'font-size:12px;opacity:0.7;';
      nameLabel.textContent = subKey.split(':').pop() || subKey;

      const valueLabel = document.createElement('span');
      valueLabel.style.cssText = 'font-size:12px;font-family:monospace;color:#4fc3f7;';
      valueLabel.textContent = `${currentSplitAt} / ${maxVal}`;

      labelRow.appendChild(nameLabel);
      labelRow.appendChild(valueLabel);
      wrapper.appendChild(labelRow);

      const slider = document.createElement('input');
      slider.type = 'range';
      slider.min = '0';
      slider.max = String(maxVal);
      slider.value = String(currentSplitAt);
      slider.style.cssText = 'width:100%;accent-color:#4fc3f7;cursor:pointer;';

      slider.addEventListener('input', () => {
        const val = parseInt(slider.value, 10);
        valueLabel.textContent = `${val} / ${maxVal}`;
        manager.setSplitAt(subKey, val);
      });

      wrapper.appendChild(slider);
      slidersContainer.appendChild(wrapper);
    }
  };

  window.setInterval(refresh, 1000);
}

/**
 * Frame-time profiling overlay. Only visible when admin mode is active.
 * Shows update/draw/total ms, FPS, model count, drawable count.
 */
function setupProfilingOverlay(): void {
  const overlay = document.createElement('div');
  overlay.id = 'perf-overlay';
  Object.assign(overlay.style, {
    position: 'fixed',
    top: '16px',
    left: '16px',
    width: '240px',
    background: 'rgba(0, 0, 0, 0.75)',
    backdropFilter: 'blur(6px)',
    color: '#fff',
    fontFamily: "'Courier New', monospace",
    fontSize: '12px',
    padding: '10px 12px',
    borderRadius: '10px',
    boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
    zIndex: '99999',
    display: 'none',
    userSelect: 'none',
    lineHeight: '1.6',
  });
  document.body.appendChild(overlay);

  const title = document.createElement('div');
  title.textContent = '⚡ Frame Profiler';
  title.style.cssText = 'font-weight:700;font-size:13px;margin-bottom:6px;color:#ffb74d;';
  overlay.appendChild(title);

  const stats = document.createElement('pre');
  stats.style.cssText = 'margin:0;white-space:pre;font-size:11px;';
  overlay.appendChild(stats);

  // Rolling averages
  const WINDOW = 60;
  const history: { total: number; update: number; draw: number }[] = [];
  let lastTs = performance.now();
  let frameCount = 0;
  let fps = 0;

  const refresh = () => {
    // Only show when admin mode is on
    const panel = document.getElementById('control-panel');
    const isAdmin = panel?.classList.contains('admin-mode') ?? false;
    if (!isAdmin) {
      overlay.style.display = 'none';
      return;
    }

    const manager = LAppLive2DManager.getInstance();
    if (!manager) {
      overlay.style.display = 'none';
      return;
    }

    overlay.style.display = '';

    const p = manager.lastFrameProfile;
    history.push({ total: p.totalMs, update: p.updateMs, draw: p.drawMs });
    if (history.length > WINDOW) history.shift();

    // Compute averages
    const avg = (fn: (h: typeof history[0]) => number) =>
      history.reduce((s, h) => s + fn(h), 0) / history.length;
    const avgTotal = avg(h => h.total);
    const avgUpdate = avg(h => h.update);
    const avgDraw = avg(h => h.draw);

    // FPS counter
    frameCount++;
    const now = performance.now();
    if (now - lastTs >= 1000) {
      fps = frameCount;
      frameCount = 0;
      lastTs = now;
    }

    const bar = (ms: number, max: number) => {
      const len = Math.round(Math.min(ms / max, 1) * 16);
      const color = ms > 16.67 ? '#ff5252' : ms > 8 ? '#ffb74d' : '#69f0ae';
      return `<span style="color:${color}">${'█'.repeat(len)}${'░'.repeat(16 - len)}</span>`;
    };

    stats.innerHTML = [
      `FPS:       <b style="color:${fps < 30 ? '#ff5252' : fps < 55 ? '#ffb74d' : '#69f0ae'}">${fps}</b>`,
      `Total:     ${avgTotal.toFixed(2)}ms ${bar(avgTotal, 33)}`,
      `  Update:  ${avgUpdate.toFixed(2)}ms ${bar(avgUpdate, 33)}`,
      `  Draw:    ${avgDraw.toFixed(2)}ms ${bar(avgDraw, 33)}`,
      `Models:    ${p.modelCount}`,
      `Passes:    ${p.passCount}`,
      `Drawables: ${p.drawableCount}`,
    ].join('\n');
  };

  // Update at ~30Hz to minimize overlay overhead
  window.setInterval(refresh, 33);
}
